import { useSelector } from "react-redux";
import { Card } from "@app/design-system/components";
import { StopIcon } from "@app/features/shared/StopIcon";
import { CustomFieldsNavButton } from "@app/features/shared/CustomFieldsNavButton";
import { StopCompletedBadge } from "@app/features/shared/StopCompletedBadge";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import styles from "./StopRow.module.css";

export type StopRowProps = {
  kind: "pickup" | "dropoff";
  /** Route position, Dropoff-only and Ondemand-only — appended to the title
   * as "Dropoff N", mirroring `DropoffMarker`'s own `sequence` prop (the map
   * marker's identical Ondemand-only affordance). Pickup never gets a
   * number: an order only ever has one. */
  sequence?: number;
  address: string;
  /** Epoch ms this stop was completed, if it has been — renders a checkmark
   * + relative time next to the title when present, hidden otherwise. */
  completedAt?: number;
  /** Proofs + custom fields on this stop, summed into the one trailing
   * badge — gates the badge/arrow nav control exactly like
   * CustomerInfoRow/DriverCard/LastEventCard gate on their own counts. */
  detailsCount: number;
  onClick: () => void;
};

/**
 * One Pickup or Dropoff entry in Order Details' stop list — its own grouped
 * section (see `Card`'s `grouped` prop), stacking with every other section
 * on the page. Only the trailing badge + arrow is clickable, navigating to
 * that stop's Details page; the rest of the row is inert, same rule as
 * CustomerInfoRow/DriverCard.
 */
export const StopRow = ({ kind, sequence, address, completedAt, detailsCount, onClick }: StopRowProps) => {
  const language = useSelector((state: RootState) => state.setting.language);
  const hasDetails = detailsCount > 0;
  const title =
    kind === "pickup"
      ? t("Pickup", language)
      : sequence !== undefined
        ? `${t("Dropoff", language)} ${sequence}`
        : t("Dropoff", language);

  return (
    <Card padded={false} grouped>
      <div className={styles.row}>
        <StopIcon kind={kind} className={styles.icon} />
        <div className={styles.body}>
          <div className={styles.header}>
            <span className={styles.title}>{title}</span>
            {completedAt !== undefined ? <StopCompletedBadge completedAt={completedAt} /> : null}
          </div>
          <div className={styles.address}>{address}</div>
        </div>
        {hasDetails ? (
          <CustomFieldsNavButton
            count={detailsCount}
            onClick={onClick}
            ariaLabel={`${t("ViewStopDetails", language)} (${detailsCount})`}
          />
        ) : null}
      </div>
    </Card>
  );
};
